import { ChangeDetectionStrategy, Component, ElementRef, inject, input, output, signal, viewChild } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { Subject, debounceTime, forkJoin, switchMap, tap } from 'rxjs';

import { BaseControlValueAccessor } from '../../../core/abstractions/base-control-value-accessor';
import { BaseEntityModel } from '../../../core/abstractions/base-entity.model';
import { BaseEntityResource } from '../../../core/abstractions/base-entity.resource';
import { EntityTypeEnum } from '../../../models/enums/entity-type.enum';
import { ListShareholderModel } from '../../../models/list-shareholder.model';
import { ListTraderModel } from '../../../models/list-trader.model';
import { ListTransactionModel } from '../../../models/list-transaction.model';
import { ReportModel } from '../../../models/report.model';
import { UserModel } from '../../../models/user.model';
import { ReportsResource } from '../../../services/resources/reports.resource';
import { ShareholdersResource } from '../../../services/resources/shareholders.resource';
import { TradersResource } from '../../../services/resources/traders.resource';
import { TransactionsResource } from '../../../services/resources/transactions.resource';
import { UsersResource } from '../../../services/resources/users.resource';

@Component({
    selector: 'sl-entity-autocomplete',
    templateUrl: './entity-autocomplete.component.html',
    styleUrls: ['./entity-autocomplete.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: EntityAutocompleteComponent
        }
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class EntityAutocompleteComponent extends BaseControlValueAccessor<string | string[]> {
    readonly type = input.required<EntityTypeEnum>();
    readonly isMultiSelect = input(false);

    readonly valueChange = output<BaseEntityModel | BaseEntityModel[] | undefined>();

    readonly selectedItems = signal<BaseEntityModel[]>([]);
    readonly isLoading = signal(false);
    readonly isSearching = signal(false);
    readonly currentValue = signal<string | BaseEntityModel>('');
    readonly filteredItems = signal<BaseEntityModel[]>([]);
    readonly disabled = signal(false);

    private readonly trigger = viewChild.required<string, ElementRef<HTMLInputElement>>('trigger', { read: ElementRef });

    private get resource(): BaseEntityResource<BaseEntityModel> {
        switch (this.type()) {
            case EntityTypeEnum.Shareholder:
                return this.shareholdersResource;
            case EntityTypeEnum.User:
                return this.usersResource;
            case EntityTypeEnum.Report:
                return this.reportsResource;
            case EntityTypeEnum.Trader:
                return this.tradersResource;
            case EntityTypeEnum.Transaction:
                return this.transactionsResource;
        }
    }

    private readonly searchSubject = new Subject<string>();

    private readonly shareholdersResource = inject(ShareholdersResource);
    private readonly usersResource = inject(UsersResource);
    private readonly reportsResource = inject(ReportsResource);
    private readonly tradersResource = inject(TradersResource);
    private readonly transactionsResource = inject(TransactionsResource);

    constructor() {
        super();

        const searchSub = this.searchSubject
            .pipe(
                tap(() => this.isSearching.set(true)),
                debounceTime(250),
                switchMap(searchText => this.resource.search(searchText, 0, 20))
            )
            .subscribe(result => {
                this.isSearching.set(false);
                this.setFilteredItems(result.value);
            });
        this.addSubscriptions(searchSub);
    }

    override writeValue(ids?: string | string[]): void {
        // eslint-disable-next-line no-null/no-null
        if (ids === undefined || ids === null || ids.length === 0) {
            super.writeValue(ids);
            return;
        }

        this.isLoading.set(true);
        this.setDisabledState(true);

        const idArray = Array.isArray(ids) ? ids : [ids];
        forkJoin(idArray.map(id => this.resource.get(id)))
            .subscribe(result => {
                if (result.length > 0) {
                    if (this.isMultiSelect()) {
                        super.writeValue(result.map(r => r.value.id));
                        this.selectedItems.set(result.map(r => r.value));
                    } else {
                        super.writeValue(result[0].value.id);
                        this.currentValue.set(result[0].value);
                    }
                } else {
                    super.writeValue(undefined);
                    this.currentValue.set('');
                }
            })
            .add(() => {
                this.isLoading.set(false);
                this.setDisabledState(false);
            });
    }

    override setDisabledState(isDisabled: boolean): void {
        super.setDisabledState(isDisabled);
        this.disabled.set(isDisabled);
    }

    onInputChange(value: string | BaseEntityModel): void {
        this.currentValue.set(value);
        if (typeof value === 'string') {
            this.searchSubject.next(value);
        }
    }

    onBlur(isPanelOpen: boolean): void {
        // blur also fires on mousedown on an option, so leave clearing to onPanelClosed while the panel is open
        if (!isPanelOpen) {
            this.clearUnselectedSearch();
        }
    }

    onPanelClosed(): void {
        const inputElement = this.trigger().nativeElement;
        // the panel also closes while typing (no results / escape), only clear once focus has left the input
        if (inputElement.ownerDocument.activeElement !== inputElement) {
            this.clearUnselectedSearch();
        }
    }

    onOptionSelected(event: MatAutocompleteSelectedEvent): void {
        // options are reused (track by id), so a still-selected option would keep the selected color and checkmark
        event.option.deselect(false);
        this.selectItem(event.option.value);
    }

    toggleItem(item: BaseEntityModel): void {
        if (this.disabled()) {
            return;
        }

        if (this.isItemSelected(item)) {
            this.unselectItem(item);
            return;
        }

        this.selectedItems.update(items => [...items, item]);
        this.valueChanged(this.selectedItems().map(i => i.id));
        this.valueChange.emit(this.selectedItems());
    }

    selectItem(item: BaseEntityModel): void {
        if (this.isMultiSelect()) {
            this.currentValue.set('');
            this.toggleItem(item);
        } else {
            this.currentValue.set(item);
            this.valueChanged(item.id);
            this.valueChange.emit(item);
        }
        this.setFilteredItems([]);
    }

    isItemSelected(item: BaseEntityModel): boolean {
        return this.selectedItems().some(i => i.id === item.id);
    }

    unselectItem(item: BaseEntityModel): void {
        if (!this.isItemSelected(item)) {
            return;
        }

        this.selectedItems.update(items => items.filter(i => i.id !== item.id));
        this.valueChanged(this.selectedItems().map(i => i.id));
        this.valueChange.emit(this.selectedItems());
    }

    displayFn(item?: BaseEntityModel | null): string {
        if (!item) {
            return '';
        }

        switch (this.type()) {
            case EntityTypeEnum.Shareholder:
                return `${(item as ListShareholderModel).displayName ?? ''} (${item.id})`;
            case EntityTypeEnum.User:
                return `${(item as UserModel).firstName ?? ''} ${(item as UserModel).lastName ?? ''}`;
            case EntityTypeEnum.Report:
                return (item as ReportModel).description;
            case EntityTypeEnum.Trader:
                return (item as ListTraderModel).displayName;
            case EntityTypeEnum.Transaction:
                // TODO better display for transactions (date, incoming, outgoing, amount, ...)
                return (item as ListTransactionModel).id;
        }
    }

    readonly displayCurrentValue = (item?: BaseEntityModel | null): string => {
        const valueToDisplay = this.isMultiSelect() ? this.currentValue() : item;
        if (typeof valueToDisplay === 'string') {
            return valueToDisplay;
        }
        return this.displayFn(valueToDisplay);
    };

    private clearUnselectedSearch(): void {
        if (typeof this.currentValue() !== 'string') {
            return;
        }

        this.currentValue.set('');
        this.setFilteredItems([]);
        if (!this.isMultiSelect()) {
            this.valueChanged(undefined);
            this.valueChange.emit(undefined);
        }
    }

    private setFilteredItems(items: BaseEntityModel[]): void {
        this.filteredItems.set(items.length > 0 ? items : this.selectedItems());
    }
}
